/**
 * The pre-send email check: every address in the audience that will not deliver,
 * or looks mistyped, with a way to fix it or drop it from this campaign.
 *
 * A fix is written back to the records the address came from — the client row,
 * the intake response, the ActiveCampaign contact — so everything else that
 * emails the person gets the corrected address too. Removing only affects this
 * campaign; the record keeps its address.
 */

import React, { useState } from 'react';
import Modal from '../Modal';
import {
  EMAIL_RECORD_LABELS,
  EmailFixError,
  fixCampaignEmail,
  type EmailCheckResult,
  type EmailIssue,
  type EmailIssueRecord,
} from '../../api/campaigns';

type RowStatus = 'open' | 'editing' | 'removed' | 'kept' | 'fixed';

interface Row {
  status: RowStatus;
  /** Records still holding the checked address. Shrinks when only some of them could be saved. */
  records: EmailIssueRecord[];
  draft: string;
  saving: boolean;
  error: string;
  /** A correction the server offered for a refused draft. */
  draftSuggestion: string | null;
  /** The draft only looks like a typo, so it can be saved as typed. */
  needsConfirm: boolean;
  /** Explains a save that reached some records but not all. */
  note: string;
  fixedTo: string;
  savedCount: number;
  optedOut: boolean;
}

const RECORD_CHIP_LIMIT = 6;

interface Props {
  userId: number;
  result: EmailCheckResult;
  /** 'send' ends in the send confirmation once every address has been dealt with. */
  purpose: 'send' | 'review';
  onRemove: (emails: string[]) => void;
  onRestore: (email: string) => void;
  /** Called with the records that now hold `to` instead of `from`. */
  onCorrected: (from: string, to: string, records: EmailIssueRecord[]) => void;
  onKeep: (email: string, keep: boolean) => void;
  onContinue: () => void;
  onClose: () => void;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const recordLabel = (r: EmailIssueRecord) =>
  r.label ? `${EMAIL_RECORD_LABELS[r.source]} · ${r.label}` : EMAIL_RECORD_LABELS[r.source];

const sameRecord = (a: { source: string; sourceId: number }, b: { source: string; sourceId: number }) =>
  a.source === b.source && a.sourceId === b.sourceId;

function saveHint(records: EmailIssueRecord[]): string {
  const stored = records.filter((r) => r.source !== 'added');
  if (stored.length === 0) return 'Saving changes the address for this campaign.';
  const where = stored.length === 1 ? 'the record listed' : `all ${stored.length} records listed`;
  return stored.some((r) => r.source === 'activecampaign')
    ? `Saving changes the address on ${where}, including the contact in ActiveCampaign.`
    : `Saving changes the address on ${where}.`;
}

const EmailCheckModal: React.FC<Props> = ({
  userId,
  result,
  purpose,
  onRemove,
  onRestore,
  onCorrected,
  onKeep,
  onContinue,
  onClose,
}) => {
  const [rows, setRows] = useState<Record<string, Row>>(() =>
    Object.fromEntries(
      result.issues.map((issue) => [
        issue.email,
        {
          status: 'open',
          records: issue.records,
          draft: '',
          saving: false,
          error: '',
          draftSuggestion: null,
          needsConfirm: false,
          note: '',
          fixedTo: '',
          savedCount: 0,
          optedOut: false,
        } as Row,
      ])
    )
  );

  const update = (email: string, patch: Partial<Row>) =>
    setRows((current) => ({ ...current, [email]: { ...current[email], ...patch } }));

  const isDone = (row: Row) => row.status === 'removed' || row.status === 'kept' || row.status === 'fixed';
  const total = result.issues.length;
  const done = result.issues.filter((i) => isDone(rows[i.email])).length;
  const anySaving = result.issues.some((i) => rows[i.email].saving);
  const invalidCount = result.issues.filter((i) => i.kind === 'invalid').length;
  const typoCount = total - invalidCount;

  const startEdit = (issue: EmailIssue) =>
    update(issue.email, {
      status: 'editing',
      draft: issue.suggestion ?? issue.email,
      error: '',
      draftSuggestion: null,
      needsConfirm: false,
    });

  const save = async (issue: EmailIssue, newEmail: string, confirmTypo = false) => {
    const records = rows[issue.email].records;
    update(issue.email, {
      status: 'editing',
      draft: newEmail,
      saving: true,
      error: '',
      draftSuggestion: null,
      needsConfirm: false,
    });
    try {
      const fix = await fixCampaignEmail(userId, { email: issue.email, newEmail, records, confirmTypo });
      const saved = records.filter(
        (r) => r.source === 'added' || fix.results.some((x) => x.ok && sameRecord(x, r))
      );
      const failed = fix.results.filter((x) => !x.ok);
      if (saved.length > 0) {
        onCorrected(issue.email, fix.email, saved);
        if (confirmTypo) onKeep(fix.email, true);
      }

      const reasons = failed
        .map((x) => {
          const record = records.find((r) => sameRecord(r, x));
          return failed.length === 1 || !record ? x.error : `${recordLabel(record)}: ${x.error}`;
        })
        .join(' ');

      if (failed.length === 0) {
        update(issue.email, {
          status: 'fixed',
          saving: false,
          fixedTo: fix.email,
          savedCount: saved.filter((r) => r.source !== 'added').length,
          optedOut: fix.optedOut,
        });
      } else if (saved.length > 0) {
        update(issue.email, {
          status: 'open',
          saving: false,
          records: records.filter((r) => failed.some((x) => sameRecord(x, r))),
          note: `Changed to ${fix.email} on ${plural(saved.length, 'record')}. The rest still have the old address. ${reasons}`,
        });
      } else {
        update(issue.email, { saving: false, error: reasons || 'Nothing was saved.' });
      }
    } catch (e) {
      update(issue.email, {
        saving: false,
        error: e instanceof Error ? e.message : 'Failed to save the address.',
        draftSuggestion: e instanceof EmailFixError ? e.suggestion : null,
        needsConfirm: e instanceof EmailFixError && e.needsConfirm,
      });
    }
  };

  const remove = (issue: EmailIssue) => {
    onRemove([issue.email]);
    update(issue.email, { status: 'removed' });
  };

  const removeAllRemaining = () => {
    const remaining = result.issues.filter((i) => !isDone(rows[i.email])).map((i) => i.email);
    if (remaining.length === 0) return;
    onRemove(remaining);
    setRows((current) => {
      const next = { ...current };
      remaining.forEach((email) => {
        next[email] = { ...next[email], status: 'removed' };
      });
      return next;
    });
  };

  const renderRecords = (records: EmailIssueRecord[]) => (
    <ul className="email-check-records">
      {records.slice(0, RECORD_CHIP_LIMIT).map((r) => (
        <li key={`${r.source}:${r.sourceId}`} className="email-check-record">
          {recordLabel(r)}
        </li>
      ))}
      {records.length > RECORD_CHIP_LIMIT ? (
        <li className="email-check-record">+{records.length - RECORD_CHIP_LIMIT} more</li>
      ) : null}
    </ul>
  );

  const renderRow = (issue: EmailIssue) => {
    const row = rows[issue.email];
    const label = issue.email;

    if (row.status === 'fixed' || row.status === 'removed' || row.status === 'kept') {
      return (
        <li key={issue.email} className="email-check-item email-check-item--done">
          <div className="email-check-item-head">
            <span className="email-check-address">{label}</span>
          </div>
          {row.status === 'fixed' ? (
            <p className="email-check-done">
              Changed to <strong>{row.fixedTo}</strong>
              {row.savedCount > 0 ? ` on ${plural(row.savedCount, 'record')}.` : ' for this campaign.'}
              {row.optedOut
                ? ' That address has opted out of marketing email, so this campaign skips it.'
                : ''}
            </p>
          ) : (
            <p className="email-check-done">
              {row.status === 'removed' ? 'Removed from this campaign.' : 'Kept as it is.'}
              <button
                type="button"
                className="email-check-link"
                onClick={() => {
                  if (row.status === 'removed') onRestore(issue.email);
                  else onKeep(issue.email, false);
                  update(issue.email, { status: 'open' });
                }}
              >
                Undo
              </button>
            </p>
          )}
        </li>
      );
    }

    return (
      <li
        key={issue.email}
        className={`email-check-item${issue.kind === 'typo' ? ' email-check-item--typo' : ''}`}
      >
        <div className="email-check-item-head">
          <span className="email-check-address">{label}</span>
          <span className={`email-check-kind email-check-kind--${issue.kind}`}>
            {issue.kind === 'invalid' ? "Won't deliver" : 'Possible typo'}
          </span>
        </div>
        <p className="email-check-problem">
          {issue.problem}
          {issue.suggestion && row.status === 'open' ? (
            <>
              {' '}
              Did you mean <strong>{issue.suggestion}</strong>?
            </>
          ) : null}
        </p>
        {renderRecords(row.records)}
        {row.note ? <p className="email-check-note">{row.note}</p> : null}

        {row.status === 'editing' ? (
          <div className="email-check-editor">
            <div className="email-check-edit-row">
              <input
                className="form-input"
                type="email"
                aria-label={`New address for ${label}`}
                value={row.draft}
                disabled={row.saving}
                autoFocus
                onChange={(e) =>
                  update(issue.email, {
                    draft: e.target.value,
                    error: '',
                    draftSuggestion: null,
                    needsConfirm: false,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && row.draft.trim()) void save(issue, row.draft.trim());
                  if (e.key === 'Escape') update(issue.email, { status: 'open', error: '' });
                }}
              />
              <button
                type="button"
                className="primary-button"
                disabled={row.saving || !row.draft.trim()}
                onClick={() => void save(issue, row.draft.trim())}
              >
                {row.saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="secondary-button"
                disabled={row.saving}
                onClick={() => update(issue.email, { status: 'open', error: '' })}
              >
                Cancel
              </button>
            </div>
            {row.error ? (
              <div className="email-check-error" role="alert">
                <span>{row.error}</span>
                {row.draftSuggestion ? (
                  <button
                    type="button"
                    className="email-check-link"
                    disabled={row.saving}
                    onClick={() => void save(issue, row.draftSuggestion as string)}
                  >
                    Use {row.draftSuggestion}
                  </button>
                ) : null}
                {row.needsConfirm ? (
                  <button
                    type="button"
                    className="email-check-link"
                    disabled={row.saving}
                    onClick={() => void save(issue, row.draft.trim(), true)}
                  >
                    Save as typed
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="email-check-hint">{saveHint(row.records)}</p>
            )}
          </div>
        ) : (
          <div className="email-check-actions">
            {issue.suggestion ? (
              <button
                type="button"
                className="primary-button"
                aria-label={`Change ${label} to ${issue.suggestion}`}
                disabled={row.saving}
                onClick={() => void save(issue, issue.suggestion as string)}
              >
                Use {issue.suggestion}
              </button>
            ) : null}
            <button
              type="button"
              className="secondary-button"
              aria-label={`Edit ${label}`}
              onClick={() => startEdit(issue)}
            >
              Edit
            </button>
            <button
              type="button"
              className="secondary-button"
              aria-label={`Remove ${label} from this campaign`}
              onClick={() => remove(issue)}
            >
              Remove
            </button>
            {issue.kind === 'typo' ? (
              <button
                type="button"
                className="secondary-button"
                aria-label={`Keep ${label} as it is`}
                onClick={() => {
                  onKeep(issue.email, true);
                  update(issue.email, { status: 'kept' });
                }}
              >
                Keep as is
              </button>
            ) : null}
          </div>
        )}
      </li>
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={purpose === 'send' ? 'Check addresses before sending' : 'Email address check'}
      wide
    >
      <div className="modal-body email-check-body">
        <p className="email-check-intro">
          {plural(total, 'address', 'addresses')} of the {result.checked.toLocaleString()} checked{' '}
          {total === 1 ? 'needs' : 'need'} a look. Fix an address to save the correction on the
          record it came from, or remove it from this campaign.
        </p>
        <div className="email-check-summary">
          {invalidCount > 0 ? (
            <span className="email-check-kind email-check-kind--invalid">
              {invalidCount} won't deliver
            </span>
          ) : null}
          {typoCount > 0 ? (
            <span className="email-check-kind email-check-kind--typo">
              {plural(typoCount, 'possible typo')}
            </span>
          ) : null}
        </div>
        {result.unchecked > 0 ? (
          <p className="email-check-hint">
            The domain of {plural(result.unchecked, 'address', 'addresses')} could not be looked up
            just now, so only {result.unchecked === 1 ? 'its' : 'their'} spelling was checked.
          </p>
        ) : null}
        {result.truncated ? (
          <p className="email-check-hint">
            The ActiveCampaign list is longer than the portal reads in one go, so only its first
            2,000 contacts were checked.
          </p>
        ) : null}
        <ul className="email-check-list">{result.issues.map(renderRow)}</ul>
      </div>
      <div className="modal-actions email-check-footer">
        <span className="email-check-progress">
          {done} of {total} dealt with
        </span>
        {done < total ? (
          <button type="button" className="secondary-button" onClick={removeAllRemaining} disabled={anySaving}>
            Remove all remaining
          </button>
        ) : null}
        {purpose === 'send' ? (
          <>
            <button type="button" className="secondary-button" onClick={onClose} disabled={anySaving}>
              Back to editing
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={onContinue}
              disabled={done < total || anySaving}
            >
              Continue to send
            </button>
          </>
        ) : (
          <button type="button" className="primary-button" onClick={onClose} disabled={anySaving}>
            Done
          </button>
        )}
      </div>
    </Modal>
  );
};

export default EmailCheckModal;
